import React from "react";
import { reportError } from "../utils/errorReporting";

interface ErrorBoundaryProps {
	children: React.ReactNode;
	route?: string;
	flowStep?: string;
	onRetry?: () => void;
}

interface ErrorBoundaryState {
	hasError: boolean;
}

const fallbackStyles = {
	container: {
		minHeight: "100vh",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "24px",
		background: "#f8f5ef",
		color: "#17352d",
	} as const,
	card: {
		maxWidth: "480px",
		width: "100%",
		padding: "32px",
		borderRadius: "20px",
		background: "#ffffff",
		boxShadow: "0 18px 50px rgba(23, 53, 45, 0.12)",
	} as const,
	headline: {
		margin: "0 0 12px",
		fontSize: "32px",
		lineHeight: 1.1,
	} as const,
	body: {
		margin: "0 0 24px",
		fontSize: "16px",
		lineHeight: 1.5,
	} as const,
	buttonRow: {
		display: "flex",
		gap: "12px",
		flexWrap: "wrap" as const,
	} as const,
	primaryButton: {
		padding: "12px 18px",
		border: "none",
		borderRadius: "999px",
		background: "#17352d",
		color: "#ffffff",
		cursor: "pointer",
		fontSize: "16px",
	} as const,
	secondaryButton: {
		padding: "12px 18px",
		borderRadius: "999px",
		border: "1px solid #17352d",
		background: "transparent",
		color: "#17352d",
		cursor: "pointer",
		fontSize: "16px",
	} as const,
};

class ErrorBoundary extends React.Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	state: ErrorBoundaryState = {
		hasError: false,
	};

	static getDerivedStateFromError(): ErrorBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		reportError(error, {
			route: this.props.route,
			flowStep: this.props.flowStep,
			component: "ErrorBoundary",
			action: "render_failure",
			metadata: {
				componentStack: errorInfo.componentStack,
			},
		});
	}

	componentDidUpdate(prevProps: ErrorBoundaryProps) {
		if (this.state.hasError && prevProps.route !== this.props.route) {
			this.setState({ hasError: false });
		}
	}

	handleTryAgain = () => {
		this.setState({ hasError: false });
		this.props.onRetry?.();
	};

	handleReload = () => {
		if (typeof window !== "undefined") {
			window.location.reload();
		}
	};

	render() {
		if (!this.state.hasError) {
			return this.props.children;
		}

		return (
			<div style={fallbackStyles.container}>
				<div style={fallbackStyles.card}>
					<h1 style={fallbackStyles.headline}>Something went wrong</h1>
					<p style={fallbackStyles.body}>
						The page hit an unexpected problem. You can try the page again or
						reload to restart the flow.
					</p>
					<div style={fallbackStyles.buttonRow}>
						<button
							type="button"
							style={fallbackStyles.primaryButton}
							onClick={this.handleTryAgain}
						>
							Try again
						</button>
						<button
							type="button"
							style={fallbackStyles.secondaryButton}
							onClick={this.handleReload}
						>
							Reload page
						</button>
					</div>
				</div>
			</div>
		);
	}
}

export default ErrorBoundary;